// Lightweight, conservative PII heuristics for admin-side review.
// This is a guardrail to nudge the admin — not a guarantee.
// We deliberately err on the side of false positives.

export interface PiiHit {
  kind: string;
  match: string;
}

const patterns: { kind: string; re: RegExp }[] = [
  { kind: "email", re: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g },
  // North-American style phone numbers
  {
    kind: "phone",
    re: /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/g,
  },
  // Street addresses: "123 Maple St", "1840 Liberty Ave"
  {
    kind: "address",
    re: /\b\d{1,5}\s+(?:[A-Z][a-z]+\s+){0,3}(?:St|Street|Ave|Avenue|Blvd|Boulevard|Rd|Road|Dr|Drive|Ln|Lane|Way|Ct|Court|Pl|Place|Pkwy|Parkway)\b\.?/g,
  },
  // Plate-like alphanumerics, e.g. ABC1234 / 7XYZ123
  { kind: "plate", re: /\b[A-Z0-9]{6,8}\b/g },
];

const NAME_RE =
  /\b(?:Pastor|Deacon|Officer|Sgt\.?|Sergeant|Mr\.?|Mrs\.?|Ms\.?|Dr\.?|Principal)\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/g;

export function detectPii(text: string): PiiHit[] {
  if (!text) return [];
  const hits: PiiHit[] = [];
  for (const { kind, re } of patterns) {
    const matches = text.match(re);
    if (matches) {
      for (const m of matches) {
        hits.push({ kind, match: m });
      }
    }
  }
  const titled = text.match(NAME_RE);
  if (titled) {
    for (const m of titled) hits.push({ kind: "title+name", match: m });
  }
  // Dedupe.
  const seen = new Set<string>();
  return hits.filter((h) => {
    const key = `${h.kind}:${h.match}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
